import { Component, effect, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  AdminButtonComponent,
  AdminModalComponent,
} from '../../admin';
import {
  APPLICANT_DOCUMENT_CATEGORIES,
  type ApplicantDocumentCategory,
  type UploadApplicantDocumentItem,
} from '../../services/applicant-documents.service';

type UploadRow = {
  file: File;
  documentName: string;
  category: ApplicantDocumentCategory | '';
};

const ACCEPTED_MIME_TYPES = new Set([
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/jpg',
  'image/png',
]);

const MAX_FILE_BYTES = 10 * 1024 * 1024;
const MAX_BATCH = 20;

@Component({
  selector: 'app-applicant-documents-upload-modal',
  standalone: true,
  imports: [FormsModule, AdminModalComponent, AdminButtonComponent],
  template: `
    @if (open()) {
      <app-admin-modal title="Upload Documents" size="lg" (closed)="onClose()">
        <div class="upload-modal">
          <p class="upload-modal__hint">
            Add one or more files. Each document needs a name. Supported formats: PDF, DOCX, JPG, JPEG, PNG (max 10 MB each).
          </p>

          <div class="upload-modal__picker">
            <input
              #fileInput
              class="upload-modal__file-input"
              type="file"
              multiple
              accept=".pdf,.docx,.jpg,.jpeg,.png,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png"
              (change)="onFilesSelected($event)"
            />
            <app-admin-button variant="secondary" type="button" [disabled]="busy()" (click)="fileInput.click()">
              Choose files
            </app-admin-button>
            @if (rows().length > 0) {
              <span class="upload-modal__file-count">{{ rows().length }} file{{ rows().length === 1 ? '' : 's' }} selected</span>
            }
          </div>

          @if (rows().length > 0) {
            <div class="upload-modal__rows">
              @for (row of rows(); track row.file.name + row.file.size; let i = $index) {
                <div class="upload-modal__row">
                  <div class="upload-modal__file-meta">
                    <strong class="upload-modal__file-name">{{ row.file.name }}</strong>
                    <span class="upload-modal__file-size">{{ formatFileSize(row.file.size) }}</span>
                  </div>
                  <label class="cg-admin-field upload-modal__field">
                    <span class="cg-admin-field__label">Document name <span class="upload-modal__required">*</span></span>
                    <input
                      class="cg-input"
                      type="text"
                      [name]="'documentName' + i"
                      [(ngModel)]="row.documentName"
                      [disabled]="busy()"
                      maxlength="200"
                      placeholder="Enter document name"
                    />
                  </label>
                  <label class="cg-admin-field upload-modal__field">
                    <span class="cg-admin-field__label">Category</span>
                    <select class="cg-select" [name]="'category' + i" [(ngModel)]="row.category" [disabled]="busy()">
                      <option value="">Select category (optional)</option>
                      @for (category of categories; track category) {
                        <option [value]="category">{{ category }}</option>
                      }
                    </select>
                  </label>
                  <app-admin-button
                    variant="ghost"
                    type="button"
                    [disabled]="busy()"
                    (click)="removeRow(i)"
                  >
                    Remove
                  </app-admin-button>
                </div>
              }
            </div>
          }

          @if (validationError()) {
            <p class="upload-modal__error" role="alert">{{ validationError() }}</p>
          }
          @if (error()) {
            <p class="upload-modal__error" role="alert">{{ error() }}</p>
          }

          <div class="upload-modal__actions">
            <app-admin-button variant="cancel" type="button" [disabled]="busy()" (click)="onClose()">
              Cancel
            </app-admin-button>
            <app-admin-button
              variant="primary"
              type="button"
              [disabled]="busy() || rows().length === 0"
              (click)="onSubmit()"
            >
              {{ busy() ? 'Uploading…' : 'Upload documents' }}
            </app-admin-button>
          </div>
        </div>
      </app-admin-modal>
    }
  `,
  styles: `
    .upload-modal {
      display: flex;
      flex-direction: column;
      gap: var(--cg-space-4);
    }
    .upload-modal__hint {
      margin: 0;
      color: var(--cg-color-text-muted);
      font-size: 0.875rem;
      line-height: 1.5;
    }
    .upload-modal__picker {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: var(--cg-space-3);
    }
    .upload-modal__file-input {
      display: none;
    }
    .upload-modal__file-count {
      font-size: 0.8125rem;
      color: var(--cg-color-text-muted);
    }
    .upload-modal__rows {
      display: flex;
      flex-direction: column;
      gap: var(--cg-space-3);
      max-height: 24rem;
      overflow: auto;
      padding-right: var(--cg-space-1);
    }
    .upload-modal__row {
      display: grid;
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
      gap: var(--cg-space-3);
      align-items: end;
      padding: var(--cg-space-3);
      border: 1px solid var(--cg-color-border);
      border-radius: var(--cg-radius-md);
      background: var(--cg-color-surface-muted);
    }
    .upload-modal__file-meta {
      grid-column: 1 / -1;
      display: flex;
      flex-wrap: wrap;
      align-items: baseline;
      gap: var(--cg-space-2);
    }
    .upload-modal__file-name {
      font-size: 0.875rem;
      color: var(--cg-color-text);
      word-break: break-word;
    }
    .upload-modal__file-size {
      font-size: 0.75rem;
      color: var(--cg-color-text-muted);
    }
    .upload-modal__field {
      min-width: 0;
    }
    .upload-modal__required {
      color: var(--cg-color-danger);
    }
    .upload-modal__error {
      margin: 0;
      color: var(--cg-color-danger);
      font-size: 0.875rem;
      font-weight: 500;
    }
    .upload-modal__actions {
      display: flex;
      justify-content: flex-end;
      gap: var(--cg-space-3);
      padding-top: var(--cg-space-2);
    }
    @media (max-width: 48rem) {
      .upload-modal__row {
        grid-template-columns: 1fr;
      }
    }
  `,
})
export class ApplicantDocumentsUploadModalComponent {
  readonly open = input(false);
  readonly busy = input(false);
  readonly error = input<string | null>(null);

  readonly confirmed = output<{ files: File[]; items: UploadApplicantDocumentItem[] }>();
  readonly closed = output<void>();

  readonly categories = APPLICANT_DOCUMENT_CATEGORIES;
  readonly rows = signal<UploadRow[]>([]);
  readonly validationError = signal<string | null>(null);

  constructor() {
    effect(() => {
      if (!this.open()) {
        this.rows.set([]);
        this.validationError.set(null);
      }
    });
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const selected = Array.from(input.files ?? []);
    input.value = '';

    if (!selected.length) return;

    const current = this.rows();
    const remaining = MAX_BATCH - current.length;
    if (remaining <= 0) {
      this.validationError.set(`You can upload up to ${MAX_BATCH} files at once.`);
      return;
    }

    const next = [...current];
    const toAdd = selected.slice(0, remaining);

    for (const file of toAdd) {
      const mime = (file.type || '').toLowerCase();
      const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
      const allowedExtensions = new Set(['pdf', 'docx', 'jpg', 'jpeg', 'png']);
      if (!ACCEPTED_MIME_TYPES.has(mime) && !allowedExtensions.has(extension)) {
        this.validationError.set('Only PDF, DOCX, JPG, JPEG, and PNG files are supported.');
        continue;
      }
      if (file.size > MAX_FILE_BYTES) {
        this.validationError.set('Each file must be 10 MB or smaller.');
        continue;
      }
      next.push({
        file,
        documentName: defaultDocumentName(file.name),
        category: '',
      });
    }

    this.rows.set(next);
    if (selected.length > remaining) {
      this.validationError.set(`Only ${remaining} more file${remaining === 1 ? '' : 's'} can be added in this batch.`);
    } else {
      this.validationError.set(null);
    }
  }

  removeRow(index: number): void {
    const next = [...this.rows()];
    next.splice(index, 1);
    this.rows.set(next);
    this.validationError.set(null);
  }

  onSubmit(): void {
    const rows = this.rows();
    if (!rows.length) {
      this.validationError.set('Select at least one file to upload.');
      return;
    }

    for (const row of rows) {
      if (!row.documentName.trim()) {
        this.validationError.set('Every document must have a name.');
        return;
      }
    }

    this.validationError.set(null);
    this.confirmed.emit({
      files: rows.map((row) => row.file),
      items: rows.map((row) => ({
        documentName: row.documentName.trim(),
        category: row.category || undefined,
      })),
    });
  }

  onClose(): void {
    if (this.busy()) return;
    this.closed.emit();
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}

function defaultDocumentName(fileName: string): string {
  const base = fileName.replace(/\.[^.]+$/, '').trim();
  return base || fileName;
}
